import React from 'react'
import { safeHtml } from '@library/dom'

import {
  AlignItems,
  Box,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { ValueBannerContentBlock } from '@library/api-menu-experience'

const HIGHLIGHT_COLOR_VARIABLE = '--value-banner-highlight-color'

const Message = withCitrusTheme(Text, () => ({
  '& .highlight': {
    border: `1px solid var(${HIGHLIGHT_COLOR_VARIABLE})`,
    borderRadius: '4px',
    color: `var(${HIGHLIGHT_COLOR_VARIABLE})`,
    fontWeight: FontWeight.Bold,
    padding: '0 4px',
  },
}))

// The API only sends `<span class="highlight">` so nothing else is allowed through

type ValueBannerProps = {
  contentBlock: Pick<
    ValueBannerContentBlock,
    'descriptionHTML' | 'color' | 'backgroundColor' | 'highlightColor' | 'supportingText'
  >
}

// webclient has no dark theme, so only the light values from the API are used
export const ValueBanner = ({ contentBlock }: ValueBannerProps) => {
  const { descriptionHTML, color, backgroundColor, highlightColor, supportingText } = contentBlock
  const highlight = (highlightColor ?? color).light

  return (
    <Box paddingBottom={6}>
      <Box
        alignItems={
          supportingText ? [AlignItems.FlexStart, null, AlignItems.Center] : AlignItems.Center
        }
        data-testid="value-banner"
        display={Display.Flex}
        gap={2}
        justifyContent={JustifyContent.Center}
        paddingH={4}
        paddingV={3}
        style={
          {
            [HIGHLIGHT_COLOR_VARIABLE]: highlight,
            backgroundColor: backgroundColor.light,
            borderBottomColor: highlight,
            borderBottomStyle: 'solid',
            borderBottomWidth: '1px',
            color: color.light,
          } as React.CSSProperties
        }
        width="100%"
      >
        {/* Flex stops the inline svg sitting on the text baseline, which pushes the icon above centre */}
        <Box data-testid="value-banner-icon" display={Display.Flex} style={{ color: highlight }}>
          <Icon name="offer_pound" size={8} variant={IconVariant.Inherit} />
        </Box>
        <Box
          alignItems={[AlignItems.FlexStart, null, AlignItems.Center]}
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
          gap={[0, null, 3]}
          wrap
        >
          <Message
            fontFamily={FontFamily.Bold}
            fontWeight={FontWeight.Bold}
            size={4}
            style={{ color: color.light }}
            dangerouslySetInnerHTML={safeHtml(descriptionHTML)}
          />
          {supportingText && (
            <Text
              data-testid="value-banner-supporting-text"
              size={2}
              style={{ color: color.light }}
            >
              {supportingText}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  )
}
